import { pendingNavHrefAtom, pendingNavRequestAtom } from '@/atoms/sideNavAtoms';
import { useAtomValue, useSetAtom, useStore } from 'jotai';
import { useRouter } from 'next/router';
import { useCallback, useEffect } from 'react';

/** Pending sidebar destination, cleared when Next accepts or rejects the route.
 * Shared with the content cover so mobile and sidebar-less layouts also recover. */
export function usePendingNavHref(): string | null {
  const router = useRouter();
  const navStore = useStore();
  const pendingNavHref = useAtomValue(pendingNavHrefAtom);
  const setPendingNavHref = useSetAtom(pendingNavHrefAtom);

  useEffect(() => {
    // routeChangeComplete fires with a locale-prefixed URL (e.g. /en/scribe/calculators)
    // for non-default locale users, but pendingNavHref is always stored without a locale
    // prefix (matching router.asPath which Next.js always strips of locale). Using
    // router.asPath here ensures the comparison is always apples-to-apples.
    const clearOnComplete = () => {
      // The owning router promise disambiguates repeated requests to one URL.
      if (navStore.get(pendingNavRequestAtom) !== null) return;
      // router.asPath is locale-free and matches how pendingNavHref is stored.
      // Strip query string and hash so that pages that append ?tab=past or #section
      // after navigation don't prevent the atom from clearing.
      const asPathBase = router.asPath.replace(/[?#].*$/, '');
      setPendingNavHref((prev) => (prev === asPathBase ? null : prev));
    };

    // routeChangeError also fires with the locale-prefixed URL, so normalise it the
    // same way before comparing. This preserves the rapid-click safety property: a
    // cancelled navigation (A) must not clobber the pending href for a subsequent
    // navigation (B) that is still in-flight.
    const clearOnError = (_err: unknown, failedUrl?: string) => {
      if (navStore.get(pendingNavRequestAtom) !== null) return;
      const locale = router.locale;
      const defaultLocale = router.defaultLocale;
      const localePrefix = locale && locale !== defaultLocale ? `/${locale}` : '';
      // Recording and discard guards can emit an error without a URL.
      // Restore the current page in that case instead of leaving its cover up.
      if (!failedUrl) {
        setPendingNavHref(null);
        return;
      }
      const withoutLocale =
        failedUrl && localePrefix && failedUrl.startsWith(localePrefix)
          ? failedUrl.slice(localePrefix.length) || '/'
          : failedUrl;
      const normalizedUrl = withoutLocale?.replace(/[?#].*$/, '');
      setPendingNavHref((prev) => (prev === normalizedUrl ? null : prev));
    };

    router.events.on('routeChangeComplete', clearOnComplete);
    router.events.on('routeChangeError', clearOnError);
    return () => {
      router.events.off('routeChangeComplete', clearOnComplete);
      router.events.off('routeChangeError', clearOnError);
    };
  }, [
    navStore,
    router.events,
    router.asPath,
    router.locale,
    router.defaultLocale,
    setPendingNavHref,
  ]);

  return pendingNavHref;
}

/** Prefix matching with the pending sidebar destination taking precedence. */
export function useIsRouteActive(): (href: string) => boolean {
  const router = useRouter();
  const pendingNavHref = usePendingNavHref();

  return useCallback(
    (href: string) => {
      if (pendingNavHref !== null) {
        return pendingNavHref.startsWith(href);
      }
      return router.asPath.startsWith(href);
    },
    [pendingNavHref, router.asPath]
  );
}
